import { usePayoutMethods } from '@/hooks/usePayoutMethods';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { CheckCircle, MoreVertical, CreditCard, Building2, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

export const LinkedAccountsList = () => {
  const { payoutMethods, loading, removePayoutMethod, setDefaultPayoutMethod } = usePayoutMethods();

  const handleSetDefault = async (methodId: string) => {
    try {
      await setDefaultPayoutMethod(methodId);
      toast({ 
        title: 'Success', 
        description: 'Default payout method updated' 
      });
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update default method',
        variant: 'destructive'
      });
    }
  };

  const handleRemove = async (methodId: string) => {
    try {
      await removePayoutMethod(methodId);
      toast({ 
        title: 'Success', 
        description: 'Payment account removed' 
      });
    } catch (error: any) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to remove account',
        variant: 'destructive'
      });
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'paypal':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.72 4.566a.562.562 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.93.93 0 0 1 .918-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
          </svg>
        );
      case 'debit_card':
        return <CreditCard className="w-5 h-5" />;
      case 'bank_account':
        return <Building2 className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mt-6 border-t pt-6">
      <h3 className="font-semibold mb-4">Connected Accounts</h3>
      
      {payoutMethods.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No payment accounts connected yet
        </p>
      ) : (
        <div className="space-y-2">
          {payoutMethods.map((method) => (
            <div 
              key={method.id} 
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                  {getIcon(method.type)}
                </div>
                
                <div>
                  <p className="font-medium">{method.account_holder_name}</p>
                  {method.type === 'paypal' && method.metadata?.paypal_email && (
                    <p className="text-sm text-muted-foreground">
                      {method.metadata.paypal_email}
                    </p>
                  )}
                  {method.last_four && (
                    <p className="text-sm text-muted-foreground">
                      ••••{method.last_four}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {method.is_default && (
                  <Badge variant="secondary" className="text-xs">
                    Default
                  </Badge>
                )}
                {method.is_verified && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!method.is_default && (
                      <DropdownMenuItem onClick={() => handleSetDefault(method.id)}>
                        Set as Default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => handleRemove(method.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      Remove Account
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
